import {
  LightDomStyles,
  MobileController,
  OutlineElement,
} from '@phase2/outline-core';

import { html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import componentStyles from './outline-global-menu-baseline.css.lit';
import outlineButtonBaselineCssLit from '../outline-button-baseline/outline-button-baseline.css.lit';

/**
 * The Global Menu component
 */
@customElement('outline-global-menu-baseline')
export class OutlineGlobalMenuBaseline extends OutlineElement {
  static styles = [componentStyles, outlineButtonBaselineCssLit];

  private mobileController = new MobileController(this, 'xl');

  lightDomStyles = new LightDomStyles(
    this,
    [componentStyles],
    this.tagName.toLowerCase()
  );

  /**
   * Aligns the dropdown to the left instead of the right.
   */
  @property({ type: Boolean, reflect: true, attribute: 'flip-alignment' })
  flipAlignment = false;

  /**
   * Sets the menu item as active.
   */
  @property({ type: Boolean, reflect: false, attribute: 'active-trail' })
  activeTrail = false;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'is-dropdown-open',
  })
  isDropdownOpen = false;

  /**
   * Used to provided a unique ID
   * for global-header component and accessibility purposes.
   */
  @property({ type: String, reflect: true })
  id: string = Math.floor(Math.random() * 10000).toString();

  firstUpdated(): void {
    this.setListeners();
  }

  connectedCallback(): void {
    super.connectedCallback();

    const isMobile = this.mobileController.isMobile;

    if (!isMobile) {
      this.handleDocumentClick();
    }

    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback(): void {
    window.removeEventListener('resize', this.handleResize);
  }

  updated(): void {
    if (this.isDropdownOpen) {
      this.isOnEdge();
    }
  }

  /**
   * Adds and removes event listeners based on device.
   */
  setListeners = () => {
    // @TODO check if mobile on screen resize.
    const isMobile = this.mobileController.isMobile;
    if (isMobile) {
      this.removeDesktopListeners();
      this.addMobileListeners();
    } else {
      this.addDesktopListeners();
      this.removeMobileListeners();
    }
  };

  /**
   * Adds mouse events when on desktop.
   */
  addDesktopListeners = () => {
    this.addEventListener('mouseenter', () => (this.isDropdownOpen = true));
    this.addEventListener('mouseleave', () => (this.isDropdownOpen = false));
    this.shadowRoot
      ?.querySelector('.dropdown-trigger')
      ?.addEventListener('keydown', this.handleKeyDown);

    // AQUA-175: Add keydown listener for navigation links.
    this.addEventListener('keydown', this.handleLinkKeyDown);
  };

  /**
   * Removes mouse events when on mobile.
   */
  removeDesktopListeners = () => {
    this.removeEventListener('mouseenter', () => (this.isDropdownOpen = true));
    this.removeEventListener('mouseleave', () => (this.isDropdownOpen = false));
  };

  /**
   * Adds mouse events when on desktop.
   */
  addMobileListeners = () => {
    this.shadowRoot
      ?.querySelector('.dropdown-trigger')
      ?.addEventListener(
        'click',
        () => (this.isDropdownOpen = !this.isDropdownOpen)
      );
  };

  /**
   * Removes mouse events when on mobile.
   */
  removeMobileListeners = () => {
    this.shadowRoot
      ?.querySelector('.dropdown-trigger')
      ?.removeEventListener(
        'click',
        () => (this.isDropdownOpen = !this.isDropdownOpen)
      );
  };

  /**
   * Closes all dropdowns if `Escape` key is clicked.
   * @param ev
   */
  handleKeyDown = (ev: Event) => {
    const { key } = ev as KeyboardEvent;

    if (key === 'Escape') {
      this.isDropdownOpen = false;
    }

    if (['Enter', ' '].includes(key)) {
      this.isDropdownOpen = !this.isDropdownOpen;
    }
  };

  /**
   * Overrides SPACE key functionality to navigate to link href.
   * @param ev
   */
  handleLinkKeyDown = (ev: Event) => {
    const { key } = ev as KeyboardEvent;
    if (key === ' ') {
      const target = ev.target as HTMLElement;
      if (target.tagName === 'A' && target.hasAttribute('href')) {
        ev.preventDefault();
        target.click();
      }
    }
  }

  /**
   * Handles clicking the trigger and outside of the component.
   */
  handleDocumentClick() {
    document.addEventListener('click', event => {
      if (!event.composedPath().includes(this)) {
        this.isDropdownOpen = false;
        return;
      }
    });
  }

  handleResize = (): void => {
    this.isOnEdge();
  };

  isOnEdge(): boolean {
    const dropdownEl = this.shadowRoot?.getElementById('dropdown');
    if (dropdownEl === null || dropdownEl === undefined) {
      return false;
    }
    const offset = window.innerWidth - dropdownEl.getBoundingClientRect().right;
    if (offset <= 0) {
      this.setAttribute('flip-alignment', 'true');
    }
    return false;
  }

  render(): TemplateResult {
    return html`
      <div
        ${this.activeTrail ? 'active-trail' : ''}
        class="${classMap({
          'global-menu': true,
        })}"
      >
        <div
          class="${classMap({
            'menu-item': true,
            'menu-is-open': this.isDropdownOpen,
            'active-trail': this.activeTrail,
          })}"
        >
          <div class="menu-link">
            <slot name="parent-item"></slot>
          </div>
          ${this.querySelector('[slot="dropdown-children"]') !== null
            ? html`
                <button
                  aria-controls="dropdown-children"
                  aria-label="button for dropdown"
                  aria-expanded="${this.isDropdownOpen}"
                  class="${classMap({
                    'dropdown-trigger': true,
                    'menu-is-open': this.isDropdownOpen,
                  })}"
                >
                  <div class="icon btn small primary">
                    <outline-icon-baseline
                      class="${this.isDropdownOpen ? 'open' : ''}"
                      name="eu-chevron"
                      library="system"
                      size="${this.mobileController.isMobile
                        ? '1rem'
                        : '0.75rem'}"
                    ></outline-icon-baseline>
                  </div>
                </button>
              `
            : null}
        </div>

        <div
          tabindex="${!this.isDropdownOpen ? '-1' : ''}"
          id="dropdown-children"
          class="${classMap({
            'dropdown-children': true,
            'is-open': this.isDropdownOpen,
          })}"
        >
          <slot name="dropdown-children"></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-global-menu-baseline': OutlineGlobalMenuBaseline;
  }
}
